import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { renderT } from '../i18n/renderT';
import ContactForm from '../components/features/ContactForm';

const Contact: React.FC = () => {
    useScrollReveal();
    const { t } = useLanguage();
    const location = useLocation();

    // Scroll to contact form when navigating with #contact-form hash
    useEffect(() => {
        if (location.hash === '#contact-form') {
            requestAnimationFrame(() => {
                // Force-reveal all elements above and including the form so they have layout
                document.querySelectorAll('.reveal').forEach(r => r.classList.add('visible'));
                const el = document.getElementById('contact-form');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            });
        }
    }, [location.hash, location.key]);

    const journeySteps = [
        {
            num: '01',
            title: t('ct.step1Title'),
            icon: (
                <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            ),
            desc: t('ct.step1Desc'),
            tags: [] as string[],
        },
        {
            num: '02',
            title: t('ct.step2Title'),
            icon: (
                <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            ),
            desc: t('ct.step2Desc'),
            tags: [] as string[],
        },
        {
            num: '03',
            title: t('ct.step3Title'),
            icon: (
                <svg viewBox="0 0 24 24"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>
            ),
            desc: t('ct.step3Desc'),
            tags: [t('ct.step3Tag1'), t('ct.step3Tag2'), t('ct.step3Tag3')],
        },
        {
            num: '04',
            title: t('ct.step4Title'),
            icon: (
                <svg viewBox="0 0 24 24"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></svg>
            ),
            desc: t('ct.step4Desc'),
            tags: [t('ct.step4Tag1'), t('ct.step4Tag2'), t('ct.step4Tag3'), t('ct.step4Tag4'), t('ct.step4Tag5')],
        },
    ];

    return (
        <main>
            <SEOHead pageId="contact" />
            {/* ── Hero ── */}
            <section className="ct-hero" id="contact-hero">
                <div className="container">
                    <div className="section-label reveal">{t('ct.heroLabel')}</div>
                    <h1 className="ct-hero-headline reveal">
                        {renderT(t('ct.heroHeadline'))}
                    </h1>
                    <p className="ct-hero-sub reveal reveal-d1">
                        {t('ct.heroSub')}
                    </p>
                    <p className="ct-hero-note reveal reveal-d2">
                        {t('ct.heroNote')}
                    </p>
                </div>
            </section>

            {/* ── Contact Form ── */}
            <ContactForm />

            {/* ── Journey ── */}
            <section className="ct-journey" id="what-next">
                <div className="container">
                    <div className="section-label reveal">{t('ct.journeyLabel')}</div>
                    <h2 className="ct-journey-headline reveal">
                        {renderT(t('ct.journeyHeadline'))}
                    </h2>

                    <div className="ct-track">
                        {journeySteps.map((step, i) => (
                            <div key={step.num} className={`ct-step reveal${i > 0 ? ` reveal-d${i}` : ''}`}>
                                <div className="ct-step-node node-orange">
                                    {step.icon}
                                </div>
                                <div className="ct-step-content">
                                    <div className="ct-step-label">{t('ct.step')} {step.num}</div>
                                    <div className="ct-step-title">{step.title}</div>
                                    <div className="ct-step-desc">{step.desc}</div>
                                    {step.tags.length > 0 && (
                                        <div className="ct-step-tags">
                                            {step.tags.map(tag => (
                                                <span key={tag} className="ct-step-tag">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* End state */}
                        <div className="ct-end-state reveal reveal-d4">
                            <div className="ct-end-node">
                                <svg viewBox="0 0 24 24">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                            </div>
                            <div className="ct-end-content">
                                <div className="ct-end-label">{t('ct.endLabel')}</div>
                                <div className="ct-end-title">
                                    {renderT(t('ct.endTitle'))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Contact;
